/**
 * RLQ — LobbyManagementCard
 *
 * Card variant used on the Management surface. Same identity strip + intent
 * + assignment + footer as the queue card, but adds an actions row at the
 * bottom (Claim / Assign… / Note / Remove) and supports bulk-select.
 *
 * The base visual is intentionally identical to `LobbyQueueCard` so the
 * supervisor sees the same anchor data with extra controls beneath.
 */

import {
  ClipboardList,
  MessageSquarePlus,
  Trash2,
  UserCheck,
  UserPlus,
} from '@retail-ops/panel-shell/fi';

import { Button } from '@/components/primitives';
import type { LobbyVisitor } from '../shared/lobbyData';
import { LobbyQueueCard } from '../queue/LobbyQueueCard';

export interface LobbyManagementCardProps {
  visitor: LobbyVisitor;
  selected: boolean;
  onToggleSelect: (v: LobbyVisitor) => void;
  onClaim: (v: LobbyVisitor) => void;
  onAssign: (v: LobbyVisitor) => void;
  onNote: (v: LobbyVisitor) => void;
  onRemove: (v: LobbyVisitor) => void;
}

export function LobbyManagementCard({
  visitor,
  selected,
  onToggleSelect,
  onClaim,
  onAssign,
  onNote,
  onRemove,
}: LobbyManagementCardProps): JSX.Element {
  return (
    <div className="lobby-mgmt-card">
      <LobbyQueueCard
        visitor={visitor}
        canClaim={!visitor.assignedOfficerName}
        onClaim={onClaim}
        selected={selected}
        onToggleSelect={onToggleSelect}
      />
      <div className="lobby-mgmt-card__actions" data-stop-card-click>
        <Button
          size="sm"
          variant={visitor.assignedOfficerName ? 'ghost' : 'primary'}
          startIcon={<UserCheck size={12} />}
          onClick={() => onClaim(visitor)}
          disabled={!!visitor.assignedOfficerName}
          title={visitor.assignedOfficerName ? 'Already with a banker' : 'Claim'}
        >
          Claim
        </Button>
        <Button
          size="sm"
          variant="ghost"
          startIcon={<UserPlus size={12} />}
          onClick={() => onAssign(visitor)}
        >
          Assign…
        </Button>
        <Button
          size="sm"
          variant="ghost"
          startIcon={<MessageSquarePlus size={12} />}
          onClick={() => onNote(visitor)}
        >
          Note
        </Button>
        <Button
          size="sm"
          variant="ghost"
          startIcon={<Trash2 size={12} />}
          onClick={() => onRemove(visitor)}
        >
          Remove
        </Button>
        {visitor._cifId ? (
          <span className="lobby-mgmt-card__hint">
            <ClipboardList size={11} aria-hidden /> Identified at check-in
          </span>
        ) : null}
      </div>
    </div>
  );
}
